import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { proxiedFetch } from "../../../../axios";
import { registerExternalFontFaceDeclarations, useAppDispatch } from "../../../../store";
import { useDebounce } from "../../../../hooks/useDebounce";

import loadFontFacesFromStyleElement from "./loadFontFacesFromStyleElement";
import { ExternalFontSubForm, SubFormProps } from "./ExternalFontSubForm";

function useGoogleQueryOptions(fontFamily: string) {
  const dispatch = useAppDispatch();

  return {
    queryKey: useDebounce(['EditStyle/GoogleFont', fontFamily], 250),
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      if (fontFamily === '') return null;

      const url = `https://fonts.googleapis.com/css2?family=${fontFamily}`;

      return proxiedFetch(url, { signal }).then(async (res) => {
        if (!res || !String(res.headers['content-type']).startsWith('text/css')) throw new Error('Invalid font');

        // We need to create a new document because:
        //   1. We don't want to add the CSS directly into the main document
        //   2. If we simply add the text into a <style> element which is not in a document, it's `.rules` will be empty
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<html><head><style>${res.data}</style></head></html>`, 'text/html');
        const style = doc.head.children[0] as HTMLStyleElement;

        return loadFontFacesFromStyleElement(style).then((map) => {
          dispatch(registerExternalFontFaceDeclarations(map));
          return map;
        });
      });
    },
  };
}

export function GoogleFontImpl(
  { setSubFormNotEmpty, state: { fontFamily, rawFontFamily, setFontFamily } }:
  SubFormProps<{ fontFamily: string, rawFontFamily: string, setFontFamily: Dispatch<SetStateAction<string>> }>
) {

  useQuery(useGoogleQueryOptions(fontFamily));

  useEffect(() => {
    setSubFormNotEmpty(fontFamily !== '');
  }, [fontFamily, setSubFormNotEmpty]);

  return (
    <>
      <label>Font Family</label>
      <input
        className="w-100"
        value={rawFontFamily}
        onChange={(e) => setFontFamily(e.currentTarget.value)}
      />
      <p>
        Enter a name of a font that can be found on&nbsp;
        <a
          href="https://fonts.google.com/"
          target="_blank"
          rel="noreferrer"
          style={{ color: 'blue' }}
        >Google Fonts</a>
      </p>
    </>
  );
}

export function GoogleFont({ visible }: { visible: boolean }) {
  const [rawFontFamily, setFontFamily] = useState('');
  const fontFamily = rawFontFamily.trim();

  return (
    <ExternalFontSubForm
      visible={visible}
      subform={GoogleFontImpl}
      queryOptions={useGoogleQueryOptions(fontFamily)}
      subformState={{ fontFamily, rawFontFamily, setFontFamily }}
    />
  );
}
